import React, { useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import MenuItem from "@mui/material/MenuItem";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";

import SaveCancel from "../../../components/SaveCancel";
import useMaterial from "../../../hooks/useMaterial";
import MaterialFields from "./components/MaterialFields";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const initialValues = {
  materialCode: "",
  materialName: "",
  categoryId: "",
  brand: "",
  model: "",
  uom: "",
  minimumStock: "",
  description: "",
  status: "ACTIVE",
};

const validationSchema = Yup.object().shape({
  materialCode: Yup.string(),
  materialName: Yup.string().required("Material Name is required"),
  categoryId: Yup.number().required("Category is required"),
  brand: Yup.string().required("Brand is required"),
  model: Yup.string(),
  uom: Yup.string().required("UOM is required"),
  minimumStock: Yup.number()
    .typeError("Minimum Stock must be a number")
    .required("Minimum Stock is required"),
  description: Yup.string(),
  status: Yup.string().required("Status is required"),
});

function AddMaterial() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });
  const { addMaterial, hasError, errorMessage, isSubmitting } = useMaterial();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
  } = methods;

  const onSubmit = async (data) => {
    try {
      await addMaterial(data);
      reset();
      dispatch(showSuccessSnackbar("Material added successfully!"));
      navigate("/inventory-mgnt/material/list", { state: { tab: 1 } });
    } catch (error) {
      console.log(error);
      dispatch(showErrorSnackbar(error.message));
    }
  };
  const cancelBtn = () => {
    navigate("/inventory-mgnt/material/list", { state: { tab: 1 } });
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Inventory</Typography>
        <Typography>Material Master</Typography>
        <Typography>Add</Typography>
      </Breadcrumbs>
    );
  };
  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_material")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <MaterialFields fields={validationSchema.fields} />
                <br></br>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddMaterial;
